import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { API_BASE_URL, STORAGE_KEYS } from '../../constants';

// Типы
export interface RailwayTariff {
  id: number;
  fromStation: string;
  toStation: string;
  wagonType?: string | null;
  minWeightKg?: number | null;
  pricePerKg: number;
  pricePerM3: number;
  isActive: boolean;
  notes?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ExpeditionTariff {
  id: number;
  minWeightKg: number;
  maxWeightKg?: number | null;
  price: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface DeliveryCostSegment {
  id?: number;
  transportType: 'AUTO' | 'RAILWAY' | 'AIR';
  fromPoint: string;
  toPoint: string;
  wagonType?: string | null;
  pricePerKg?: number | null;
  pricePerM3?: number | null;
  segmentCost: number;
  clientRate?: number | null;
  manualCost?: boolean;
  sortOrder?: number;
  notes?: string | null;
}

export interface DeliveryCostCalculation {
  id: number;
  cargoPlaces: number;
  cargoWeightKg: number;
  cargoVolumeM3: number;
  cargoLengthM?: number | null;
  cargoWidthM?: number | null;
  cargoHeightM?: number | null;
  cargoDescription?: string | null;
  totalCost: number;
  totalClientRate?: number | null;
  expeditionCost?: number | null;
  applicationId?: number | null;
  clientId?: number | null;
  client?: { id: number; name: string } | null;
  createdBy: number;
  createdByUser?: { id: number; username: string };
  segments: DeliveryCostSegment[];
  createdAt: string;
  updatedAt: string;
}

export interface CreateCalculationRequest {
  cargoPlaces: number;
  cargoWeightKg: number;
  cargoVolumeM3: number;
  cargoLengthM?: number;
  cargoWidthM?: number;
  cargoHeightM?: number;
  cargoDescription?: string;
  segments: Omit<DeliveryCostSegment, 'id'>[];
  includeExpedition?: boolean;
  customExpeditionCost?: number;
  clientId?: number;
  applicationId?: number;
}

export interface RouteStatistics {
  count: number;
  avgCost: number;
  avgPricePerKg: number;
  avgPricePerM3: number;
  recentSegments: DeliveryCostSegment[];
}

export const deliveryCostApi = createApi({
  reducerPath: 'deliveryCostApi',
  baseQuery: fetchBaseQuery({
    baseUrl: `${API_BASE_URL}/delivery-cost`,
    prepareHeaders: (headers) => {
      const token = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
      if (token) {
        headers.set('authorization', `Bearer ${token}`);
      }
      return headers;
    },
  }),
  tagTypes: ['RailwayTariff', 'ExpeditionTariff', 'Calculation'],
  endpoints: (builder) => ({
    // ЖД тарифы
    getRailwayTariffs: builder.query<RailwayTariff[], void>({
      query: () => '/tariffs/railway',
      transformResponse: (response: { success: boolean; data: RailwayTariff[] }) => response.data,
      providesTags: ['RailwayTariff'],
    }),
    createRailwayTariff: builder.mutation<RailwayTariff, Partial<RailwayTariff>>({
      query: (body) => ({
        url: '/tariffs/railway',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['RailwayTariff'],
    }),
    updateRailwayTariff: builder.mutation<RailwayTariff, { id: number } & Partial<RailwayTariff>>({
      query: ({ id, ...body }) => ({
        url: `/tariffs/railway/${id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['RailwayTariff'],
    }),
    deleteRailwayTariff: builder.mutation<void, number>({
      query: (id) => ({
        url: `/tariffs/railway/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['RailwayTariff'],
    }),

    // Тарифы экспедирования
    getExpeditionTariffs: builder.query<ExpeditionTariff[], void>({
      query: () => '/tariffs/expedition',
      transformResponse: (response: { success: boolean; data: ExpeditionTariff[] }) => response.data,
      providesTags: ['ExpeditionTariff'],
    }),
    createExpeditionTariff: builder.mutation<ExpeditionTariff, Partial<ExpeditionTariff>>({
      query: (body) => ({
        url: '/tariffs/expedition',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['ExpeditionTariff'],
    }),
    updateExpeditionTariff: builder.mutation<ExpeditionTariff, { id: number } & Partial<ExpeditionTariff>>({
      query: ({ id, ...body }) => ({
        url: `/tariffs/expedition/${id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['ExpeditionTariff'],
    }),

    // Расчёты
    getCalculations: builder.query<{
      calculations: DeliveryCostCalculation[];
      pagination: { page: number; limit: number; total: number; pages: number };
    }, { page?: number; limit?: number; clientId?: number; fromDate?: string; toDate?: string }>({
      query: (params) => ({
        url: '/calculations',
        params,
      }),
      transformResponse: (response: { success: boolean; data: any }) => response.data,
      providesTags: ['Calculation'],
    }),
    getCalculationById: builder.query<DeliveryCostCalculation, number>({
      query: (id) => `/calculations/${id}`,
      transformResponse: (response: { success: boolean; data: DeliveryCostCalculation }) => response.data,
    }),
    createCalculation: builder.mutation<DeliveryCostCalculation, CreateCalculationRequest>({
      query: (body) => ({
        url: '/calculations',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Calculation'],
    }),
    updateCalculation: builder.mutation<DeliveryCostCalculation, { id: number } & CreateCalculationRequest>({
      query: ({ id, ...body }) => ({
        url: `/calculations/${id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['Calculation'],
    }),

    // Статистика
    getRouteStatistics: builder.query<RouteStatistics, { fromPoint: string; toPoint: string; transportType?: string }>({
      query: (params) => ({
        url: '/statistics/route',
        params,
      }),
      transformResponse: (response: { success: boolean; data: RouteStatistics }) => response.data,
    }),

    // Инициализация тарифов
    seedTariffs: builder.mutation<void, void>({
      query: () => ({
        url: '/seed-tariffs',
        method: 'POST',
      }),
      invalidatesTags: ['RailwayTariff', 'ExpeditionTariff'],
    }),
  }),
});

export const {
  useGetRailwayTariffsQuery,
  useCreateRailwayTariffMutation,
  useUpdateRailwayTariffMutation,
  useDeleteRailwayTariffMutation,
  useGetExpeditionTariffsQuery,
  useCreateExpeditionTariffMutation,
  useUpdateExpeditionTariffMutation,
  useGetCalculationsQuery,
  useGetCalculationByIdQuery,
  useCreateCalculationMutation,
  useUpdateCalculationMutation,
  useGetRouteStatisticsQuery,
  useSeedTariffsMutation,
} = deliveryCostApi;

